import { useEffect, useRef, useState } from "react";

import { ApiFehler, portalDokumentHochladen } from "../../api/client";
import { usePortalEntfernen, usePortalNachUpload } from "../../api/hooks";
import type { DokumentTyp, PortalDokument } from "../../api/typen";
import { useBlobUrl } from "../../hooks/useBlobUrl";
import { useToast } from "../../kontext/ToastKontext";
import { bildVerkleinern } from "../../lib/bild";
import { zeitpunkt } from "../../lib/format";
import { Karte, Knopf, Spinner } from "../Basis";

interface KartenArt {
  typ: DokumentTyp;
  titel: string;
  hilfe: string;
  pflicht: boolean;
}

/** Die Checkliste: bewusst nur vier Karten, in Klartext. */
export const KARTEN: KartenArt[] = [
  { typ: "rechnung", titel: "Rechnung oder Angebot", hilfe: "Darauf stehen Anlagengröße, Module und Wechselrichter.", pflicht: true },
  { typ: "vollmacht", titel: "Unterschriebene Vollmacht", hilfe: "Vom Anlagenbetreiber unterschrieben.", pflicht: true },
  { typ: "formular", titel: "Formulare des Netzbetreibers", hilfe: "Falls schon ausgefüllt vorhanden.", pflicht: false },
  { typ: "foto", titel: "Fotos", hilfe: "Zählerschrank, Wechselrichter, Anlage.", pflicht: false },
];

type Phase = "vorbereiten" | "hochladen" | "speichern" | "fehler";

interface Auftrag {
  id: number;
  datei: File;
  phase: Phase;
  prozent: number;
  fehler?: string;
}

const PHASE_TEXT: Record<Exclude<Phase, "fehler">, string> = {
  vorbereiten: "Wird vorbereitet…",
  hochladen: "Wird hochgeladen…",
  speichern: "Wird gespeichert…",
};

function fehlerText(fehler: unknown): string {
  if (fehler instanceof ApiFehler) {
    if (fehler.status === 0) return "Keine Verbindung. Bitte erneut versuchen.";
    if (fehler.status === 413) return "Die Datei ist zu groß.";
    if (fehler.status === 415) return "Bitte ein Foto (JPG, PNG), eine PDF- oder Word-Datei wählen.";
    if (fehler.status === 429) return "Zu viele Uploads auf einmal. Bitte kurz warten und erneut versuchen.";
    return fehler.message;
  }
  return "Das hat nicht geklappt. Bitte erneut versuchen.";
}

function Vorschaubild({ dokument }: { dokument: PortalDokument }) {
  const { url } = useBlobUrl(dokument.thumbnail_url);
  if (url) return <img src={url} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />;
  return (
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-xs font-semibold text-neutral-500" aria-hidden>
      {dokument.dateiname.toLowerCase().endsWith(".pdf") ? "PDF" : /\.docx?$/.test(dokument.dateiname.toLowerCase()) ? "Word" : "Datei"}
    </span>
  );
}

function DokumentZeile({ dokument, beiEntfernen, entfernt }: { dokument: PortalDokument; beiEntfernen: () => void; entfernt: boolean }) {
  return (
    <li className="flex items-center gap-3 py-2">
      <Vorschaubild dokument={dokument} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{dokument.dateiname}</p>
        <p className="text-xs text-ampel-gruen">
          ✓ hochgeladen · {zeitpunkt(dokument.hochgeladen_am)}
          {dokument.nachgereicht && <span className="text-neutral-500"> · nachgereicht</span>}
        </p>
      </div>
      {dokument.entfernbar && (
        <button
          type="button"
          onClick={beiEntfernen}
          disabled={entfernt}
          aria-label={`${dokument.dateiname} entfernen`}
          className="rounded-lg px-3 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-100 disabled:opacity-50"
        >
          Entfernen
        </button>
      )}
    </li>
  );
}

function UnterlagenKarte({ art, anlageId, dokumente }: { art: KartenArt; anlageId: number; dokumente: PortalDokument[] }) {
  const toast = useToast();
  const nachUpload = usePortalNachUpload(anlageId);
  const entfernen = usePortalEntfernen();
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const zaehler = useRef(0);
  const aktiv = useRef(true);
  const warteschlange = useRef<Promise<void>>(Promise.resolve());
  const kamera = useRef<HTMLInputElement>(null);
  const auswahl = useRef<HTMLInputElement>(null);

  useEffect(() => {
    aktiv.current = true;
    return () => {
      aktiv.current = false;
    };
  }, []);

  function aendern(id: number, teil: Partial<Auftrag>) {
    if (aktiv.current) setAuftraege((alt) => alt.map((a) => (a.id === id ? { ...a, ...teil } : a)));
  }

  async function ausfuehren(auftrag: Auftrag) {
    aendern(auftrag.id, { phase: "vorbereiten", fehler: undefined, prozent: 0 });
    try {
      const datei = await bildVerkleinern(auftrag.datei);
      aendern(auftrag.id, { phase: "hochladen" });
      const lauf = portalDokumentHochladen({ datei, typ: art.typ, anlageId }, (prozent) =>
        aendern(auftrag.id, { prozent, phase: prozent >= 100 ? "speichern" : "hochladen" }),
      );
      await lauf.ergebnis;
      if (aktiv.current) setAuftraege((alt) => alt.filter((a) => a.id !== auftrag.id));
      nachUpload();
    } catch (fehler) {
      if (fehler instanceof ApiFehler && fehler.code === "duplikat") {
        if (aktiv.current) setAuftraege((alt) => alt.filter((a) => a.id !== auftrag.id));
        toast.info("Schon vorhanden", `„${auftrag.datei.name}“ wurde bereits hochgeladen.`);
        return;
      }
      aendern(auftrag.id, { phase: "fehler", fehler: fehlerText(fehler) });
    }
  }

  function hinzufuegen(liste: FileList | null) {
    if (!liste || liste.length === 0) return;
    const neu: Auftrag[] = Array.from(liste).map((datei) => ({ id: ++zaehler.current, datei, phase: "vorbereiten", prozent: 0 }));
    setAuftraege((alt) => [...alt, ...neu]);
    // Nacheinander statt gleichzeitig: schont das Handynetz und das Upload-Limit.
    for (const auftrag of neu) warteschlange.current = warteschlange.current.then(() => ausfuehren(auftrag));
  }

  function nochmal(auftrag: Auftrag) {
    warteschlange.current = warteschlange.current.then(() => ausfuehren(auftrag));
  }

  function verwerfen(id: number) {
    setAuftraege((alt) => alt.filter((a) => a.id !== id));
  }

  const vorhanden = dokumente.filter((d) => d.typ === art.typ);
  const nurFoto = art.typ === "foto";

  return (
    <Karte className="p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
            vorhanden.length > 0 ? "bg-ampel-gruen text-white" : "border-2 border-neutral-300 text-transparent"
          }`}
          aria-hidden
        >
          ✓
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold">
            {art.titel}
            {!art.pflicht && <span className="ml-2 text-xs font-normal text-neutral-500">optional</span>}
          </h3>
          <p className="text-sm text-neutral-600">{art.hilfe}</p>
        </div>
      </div>

      {vorhanden.length > 0 && (
        <ul className="mt-3 divide-y divide-neutral-100 border-t border-neutral-100">
          {vorhanden.map((d) => (
            <DokumentZeile
              key={d.id}
              dokument={d}
              entfernt={entfernen.isPending}
              beiEntfernen={() =>
                entfernen.mutate(d.id, {
                  onError: (e) => toast.fehler(e, "Entfernen nicht möglich"),
                })
              }
            />
          ))}
        </ul>
      )}

      {auftraege.length > 0 && (
        <ul className="mt-3 space-y-2" aria-live="polite">
          {auftraege.map((a) => (
            <li key={a.id} className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
              <p className="truncate font-semibold">{a.datei.name}</p>
              {a.phase === "fehler" ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="text-ampel-rot">{a.fehler}</span>
                  <Knopf className="!px-3 !py-1.5" onClick={() => nochmal(a)}>Erneut versuchen</Knopf>
                  <Knopf variante="geist" className="!px-3 !py-1.5" onClick={() => verwerfen(a.id)}>Verwerfen</Knopf>
                </div>
              ) : (
                <div className="mt-1">
                  <div className="flex items-center gap-2 text-neutral-600">
                    <Spinner klein />
                    <span>{PHASE_TEXT[a.phase]}</span>
                  </div>
                  {a.phase === "hochladen" && (
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-200" role="progressbar" aria-valuenow={a.prozent} aria-valuemin={0} aria-valuemax={100}>
                      <div className="h-full bg-marke transition-all" style={{ width: `${a.prozent}%` }} />
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <Knopf variante="primaer" className="!py-3" onClick={() => kamera.current?.click()}>
          Foto aufnehmen
        </Knopf>
        <Knopf className="!py-3" onClick={() => auswahl.current?.click()}>
          Datei wählen
        </Knopf>
      </div>
      {/* capture="environment" öffnet auf dem Handy direkt die Kamera. Auf dem Rechner öffnet sich die Dateiauswahl. */}
      <input
        ref={kamera}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid={`kamera-${art.typ}`}
        onChange={(e) => {
          hinzufuegen(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={auswahl}
        type="file"
        multiple
        accept={nurFoto ? "image/jpeg,image/png" : "image/jpeg,image/png,application/pdf,.docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"}
        className="hidden"
        data-testid={`datei-${art.typ}`}
        onChange={(e) => {
          hinzufuegen(e.target.files);
          e.target.value = "";
        }}
      />
    </Karte>
  );
}

/** Die vier Upload-Karten einer Anmeldung. Funktioniert in jedem Status (Nachreichen). */
export function UnterlagenKarten({ anlageId, dokumente }: { anlageId: number; dokumente: PortalDokument[] }) {
  return (
    <div className="space-y-3">
      {KARTEN.map((art) => (
        <UnterlagenKarte key={art.typ} art={art} anlageId={anlageId} dokumente={dokumente} />
      ))}
    </div>
  );
}
